import { useAuthState } from "react-firebase-hooks/auth";
import { useState } from "react";
import { signOut } from "firebase/auth";
import { auth } from "./firebase";
import { BASE_URL } from "./types";

// Helper function to handle 401 logout and redirect
const handleUnauthorized = async (): Promise<void> => {
  try {
    await signOut(auth);
    localStorage.clear();
    window.location.href = "/login";
  } catch {
    //    console.error("Error during logout:", error);
    window.location.href = "/login";
  }
};

export interface ApiRequestOptions extends RequestInit {
  headers?: Record<string, string>;
}

function ApiCall(endpoint: string, options: ApiRequestOptions = {}) {
  const [user, loading, error] = useAuthState(auth);
  const [data, setData] = useState(null);
  const [apiLoading, setApiLoading] = useState(false);
  //  const { user, loading } = useAuth();

  const makeAuthenticatedCall = async () => {
    if (!user) {
      //      console.error("User not authenticated");
      return;
    }

    try {
      setApiLoading(true);

      // Get the user's ID token
      const idToken = await user.getIdToken();
      const defaultHeaders: Record<string, string> = {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      };
      const config: RequestInit = {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
      };

      // Make the authenticated API call
      const response = await fetch(BASE_URL + endpoint, config);
      //   {
      //     method: "GET",
      //     headers: {
      //       Authorization: `Bearer ${idToken}`,
      //       "Content-Type": "application/json",
      //     },
      //   });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
    } catch {
      //      console.error("API call failed:", error);
    } finally {
      setApiLoading(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;
  if (!user) return <div>Please sign in</div>;

  return (
    <div>
      <h2>Authenticated API Call Example</h2>
      <button onClick={makeAuthenticatedCall} disabled={apiLoading}>
        {apiLoading ? "Loading..." : "Make API Call"}
      </button>

      {data && (
        <div>
          <h3>API Response:</h3>
          <pre>{JSON.stringify(data, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

// Helper function to get user-friendly error message
const getConnectionErrorMessage = (error: unknown): string => {
  if (error instanceof TypeError) {
    const message = error.message.toLowerCase();
    if (message.includes("failed to fetch") || message.includes("network")) {
      return "We're having trouble connecting right now. Please check your internet connection and try again in a moment.";
    }
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (message.includes("timeout") || message.includes("aborted")) {
      return "This is taking longer than expected. Please try again in a few moments.";
    }
    if (message.includes("network")) {
      return "We're having trouble connecting right now. Please check your internet connection and try again.";
    }
  }

  return "Something went wrong on our end. Please try again in a few moments.";
};

// Helper function to get user-friendly HTTP error message
const getHttpErrorMessage = (status: number): string => {
  switch (status) {
    case 400:
      return "Something doesn't look right. Please check your information and try again.";
    case 401:
      return "Your session has expired. Please log in again.";
    case 403:
      return "You don't have permission to do this.";
    case 404:
      return "We couldn't find what you're looking for.";
    case 408:
      return "This is taking longer than expected. Please try again.";
    case 429:
      return "You're doing that too quickly. Please wait a moment and try again.";
    case 500:
      return "Something went wrong on our end. Please try again in a few moments.";
    case 502:
      return "We're having trouble connecting to our servers. Please try again in a few moments.";
    case 503:
      return "We're temporarily unavailable for maintenance. Please try again shortly.";
    case 504:
      return "This is taking longer than expected. Please try again in a few moments.";
    default:
      if (status >= 500) {
        return "Something went wrong on our end. Please try again in a few moments.";
      }
      return "Something went wrong. Please try again.";
  }
};

// Static method for making authenticated API requests
ApiCall.makeAuthenticatedRequest = async (
  endpoint: string,
  options: ApiRequestOptions = {},
  useJson: boolean = true,
  timeoutMs: number = 30_000,
): Promise<Record<string, unknown>> => {
  const user = auth.currentUser;

  if (!user) {
    return { error: "User not authenticated", isAuthError: true };
  }

  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);

  try {
    // Get the user's ID token
    const idToken = await user.getIdToken();
    const defaultHeaders: Record<string, string> = {
      Authorization: `Bearer ${idToken}`,
    };
    if (useJson) {
      defaultHeaders["Content-Type"] = "application/json";
    }
    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      signal: options.signal
        ? (AbortSignal as unknown as { any: (signals: AbortSignal[]) => AbortSignal }).any([options.signal, timeoutController.signal])
        : timeoutController.signal,
    };

    // Make the authenticated API call
    const response = await fetch(BASE_URL + endpoint, config);
    clearTimeout(timeoutId);

    if (!response.ok) {
      // Handle 401 Unauthorized - sign out and redirect to login
      if (response.status === 401) {
        await handleUnauthorized();
        return {
          error: "Your session has expired. Please log in again.",
          statusCode: 401,
          isAuthError: true,
        };
      }

      let serverDetail: string | undefined;
      try {
        const errorBody = await response.json();
        serverDetail =
          errorBody?.detail || errorBody?.error || errorBody?.message;
      } catch {
        // ignore parse errors
      }

      const errorMessage = serverDetail || getHttpErrorMessage(response.status);
      return {
        error: errorMessage,
        statusCode: response.status,
        isConnectionError: response.status >= 500 || response.status === 408,
      };
    }

    const result = await response.json();
    return result as Record<string, unknown>;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === "AbortError") {
      return { error: "aborted", isAborted: true };
    }
    console.error("API call failed:", error);
    const errorMessage = getConnectionErrorMessage(error);
    return {
      error: errorMessage,
      isConnectionError: true,
    };
  }
};

export default ApiCall;
